import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, User as UserIcon, UserPlus, Crown } from "lucide-react";
import { toast } from "sonner";

export default function CreateTicket() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const isStaff = user?.role !== "end_user";
  const prefillReporter = params.get("reporter_id") || "";

  const [form, setForm] = useState({
    title: "", description: "",
    priority: "", category_id: "", subcategory: "",
    reporter_id: prefillReporter, assignee_id: "",
    ticket_type: "customer",
    internal_task_type: "",
    organization: user?.organization || "",
    department: user?.department || "",
    location: user?.location || "",
    reporter_phone: user?.phone || "",
    asset_tag: "",
    asset_id: "",
  });
  const [useAI, setUseAI] = useState(true);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Inline "New customer" dialog
  const [custOpen, setCustOpen] = useState(false);
  const [custForm, setCustForm] = useState({ name: "", email: "", phone: "", organization: "", department: "", location: "", notes: "", vip: false });
  const [custSaving, setCustSaving] = useState(false);

  const loadUsers = async () => {
    if (!isStaff) return;
    const u = await api.get("/users");
    setUsers(u.data);
  };

  useEffect(() => {
    (async () => {
      const [c, u, a] = await Promise.all([
        api.get("/categories"),
        isStaff ? api.get("/users") : Promise.resolve({ data: [] }),
        isStaff ? api.get("/assets") : Promise.resolve({ data: [] }),
      ]);
      setCategories(c.data);
      setUsers(u.data);
      setAssets(a.data);
    })();
  }, [isStaff]);

  const currentCat = categories.find((c) => c.category_id === form.category_id);
  const customers = users.filter((u) => u.role === "end_user");
  const agents = users.filter((u) => ["agent", "team_leader", "admin", "manager"].includes(u.role));
  const selectedCustomer = customers.find((c) => c.user_id === form.reporter_id);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const saveCustomer = async () => {
    if (!custForm.name.trim() || !custForm.email.trim()) { toast.error("Name and email required"); return; }
    setCustSaving(true);
    try {
      const { data } = await api.post("/customers", { ...custForm, name: custForm.name.trim(), email: custForm.email.trim().toLowerCase() });
      toast.success(`Added ${data.name}`);
      await loadUsers();
      setForm((p) => ({
        ...p,
        reporter_id: data.user_id,
        organization: p.organization || data.organization || "",
        department: p.department || data.department || "",
        location: p.location || data.location || "",
        reporter_phone: p.reporter_phone || data.phone || "",
      }));
      setCustOpen(false);
      setCustForm({ name: "", email: "", phone: "", organization: "", department: "", location: "", notes: "", vip: false });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save customer"); }
    finally { setCustSaving(false); }
  };
  const setCust = (k, v) => setCustForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error("Title and description are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        use_ai: useAI,
        ticket_type: form.ticket_type || "customer",
        organization: form.organization || null,
        department: form.department || null,
        location: form.location || null,
        reporter_phone: form.reporter_phone || null,
        asset_tag: form.asset_tag || null,
        asset_id: form.asset_id || null,
      };
      if (form.priority) payload.priority = form.priority;
      if (form.category_id) payload.category_id = form.category_id;
      if (form.subcategory) payload.subcategory = form.subcategory;
      if (isStaff && form.reporter_id) payload.reporter_id = form.reporter_id;
      if (isStaff && form.assignee_id) payload.assignee_id = form.assignee_id;
      if (form.ticket_type === "internal" && form.internal_task_type) {
        payload.internal_task_type = form.internal_task_type;
      }
      const { data } = await api.post("/tickets", payload);
      toast.success(`Ticket ${data.ticket_number} created`);
      nav(`/tickets/${data.ticket_id}`);
    } catch (e) {
      toast.error("Failed to create ticket");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-red-600">New Ticket</div>
      <h1 className="font-display text-4xl font-black tracking-tight mt-2" data-testid="create-heading">Report an issue</h1>
      <p className="text-slate-600 text-sm mt-2 max-w-lg">The more context you share, the faster we can help. AI triage will suggest category and priority if left blank.</p>

      <div className="mt-8 space-y-6 border border-slate-200 bg-white p-8">
        {isStaff && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pb-6 border-b border-slate-200">
            <Field label="Ticket type">
              <Select value={form.ticket_type} onValueChange={(v) => { set("ticket_type", v); if (v !== "internal") set("internal_task_type", ""); }}>
                <SelectTrigger className="bg-white border-slate-200 rounded-sm h-11" data-testid="ticket-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer" data-testid="type-customer">Customer request &middot; client-facing (emails fire)</SelectItem>
                  <SelectItem value="internal" data-testid="type-internal">Internal task &middot; KINOS office / no customer emails</SelectItem>
                </SelectContent>
              </Select>
              {form.ticket_type === "internal" && (
                <div className="mt-3">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-1.5">Task category</div>
                  <Select value={form.internal_task_type} onValueChange={(v) => set("internal_task_type", v)}>
                    <SelectTrigger className="bg-white border-slate-200 rounded-sm h-10" data-testid="internal-task-type-select">
                      <SelectValue placeholder="Pick a task type..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="quotation" data-testid="task-type-quotation">Quotation</SelectItem>
                      <SelectItem value="purchase_order" data-testid="task-type-po">Purchase Order</SelectItem>
                      <SelectItem value="invoice" data-testid="task-type-invoice">Invoice</SelectItem>
                      <SelectItem value="testing" data-testid="task-type-testing">App / System Testing</SelectItem>
                      <SelectItem value="proposal" data-testid="task-type-proposal">Proposal</SelectItem>
                      <SelectItem value="clean_office" data-testid="task-type-clean">Clean office</SelectItem>
                      <SelectItem value="delivery_packet" data-testid="task-type-delivery">Delivery packet</SelectItem>
                      <SelectItem value="submit_tender" data-testid="task-type-tender">Submit document / tender</SelectItem>
                      <SelectItem value="install_equipment" data-testid="task-type-install">Install KINOS IT equipment</SelectItem>
                      <SelectItem value="brochure" data-testid="task-type-brochure">Make brochure</SelectItem>
                      <SelectItem value="printing" data-testid="task-type-printing">Printing</SelectItem>
                      <SelectItem value="other" data-testid="task-type-other">Other internal task</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </Field>
            <Field label={form.ticket_type === "internal" ? "Task owner (staff)" : "On behalf of (customer)"}>
              <div className="flex gap-2">
                <div className="flex-1 min-w-0">
                  <Select value={form.reporter_id} onValueChange={(v) => set("reporter_id", v)}>
                    <SelectTrigger className="bg-white border-slate-200 rounded-sm h-11" data-testid="reporter-select">
                      <SelectValue placeholder={form.ticket_type === "internal" ? "Me" : "Myself"} />
                    </SelectTrigger>
                    <SelectContent>
                      {(form.ticket_type === "internal" ? agents : customers).length === 0 && (
                        <div className="px-3 py-2 text-xs text-slate-500">
                          {form.ticket_type === "internal" ? "No staff yet" : "No customers yet"}
                        </div>
                      )}
                      {(form.ticket_type === "internal" ? agents : customers).map((p) => (
                        <SelectItem key={p.user_id} value={p.user_id}>
                          <span className="flex items-center gap-2">
                            <UserIcon className="h-3 w-3" strokeWidth={1.5} />
                            {p.name} <span className="text-slate-500 font-mono text-xs">· {p.email}</span>
                            {p.vip && <Crown className="h-3 w-3 text-amber-400" strokeWidth={2} />}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {form.ticket_type === "customer" && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCustOpen(true)}
                    data-testid="inline-new-customer-btn"
                    className="h-11 border-slate-200 bg-transparent hover:bg-slate-100 rounded-sm text-xs whitespace-nowrap"
                    title="Create a new customer profile inline"
                  >
                    <UserPlus className="h-3.5 w-3.5" strokeWidth={1.8} />
                    New
                  </Button>
                )}
              </div>
              {selectedCustomer?.vip && (
                <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-0.5 border rounded-sm bg-amber-500/15 text-amber-300 border-amber-500/40" data-testid="reporter-vip-hint">
                  <Crown className="h-3 w-3" strokeWidth={2} /> VIP customer · prioritize
                </div>
              )}
            </Field>
            <Field label={form.ticket_type === "internal" ? "Second assignee (optional)" : "Assign to (agent)"}>
              <Select value={form.assignee_id} onValueChange={(v) => set("assignee_id", v)}>
                <SelectTrigger className="bg-white border-slate-200 rounded-sm h-11" data-testid="assignee-select">
                  <SelectValue placeholder="Auto (by category)" />
                </SelectTrigger>
                <SelectContent>
                  {agents.map((a) => (
                    <SelectItem key={a.user_id} value={a.user_id}>{a.name} <span className="text-slate-500 font-mono text-xs">· {a.role}</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
        )}

        <Field label="Subject">
          <Input data-testid="ticket-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Cannot connect to KINOS VPN from home" className="bg-white border-slate-200 rounded-sm h-11" />
        </Field>
        <Field label="Detailed description">
          <Textarea data-testid="ticket-description-input" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Steps to reproduce, error messages, when it started..." rows={6} className="bg-white border-slate-200 rounded-sm" />
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Field label="Category">
            <Select value={form.category_id} onValueChange={(v) => { set("category_id", v); set("subcategory", ""); }}>
              <SelectTrigger className="bg-white border-slate-200 rounded-sm h-11" data-testid="ticket-category-select">
                <SelectValue placeholder="Auto (AI pick)" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (<SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Subcategory">
            <Select value={form.subcategory} onValueChange={(v) => set("subcategory", v)} disabled={!currentCat?.subcategories?.length}>
              <SelectTrigger className="bg-white border-slate-200 rounded-sm h-11" data-testid="ticket-subcategory-select">
                <SelectValue placeholder={currentCat ? "Select" : "Pick category first"} />
              </SelectTrigger>
              <SelectContent>
                {(currentCat?.subcategories || []).map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Priority">
            <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger className="bg-white border-slate-200 rounded-sm h-11" data-testid="ticket-priority-select">
                <SelectValue placeholder="Auto (AI)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low · 4h response / 3d resolve</SelectItem>
                <SelectItem value="medium">Medium · 2h / 24h</SelectItem>
                <SelectItem value="high">High · 30m / 8h</SelectItem>
                <SelectItem value="critical">Critical · 15m / 4h</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Organization">
            <Input value={form.organization} onChange={(e) => set("organization", e.target.value)} placeholder="e.g. KINOS HQ" className="bg-white border-slate-200 rounded-sm h-11" data-testid="ticket-organization" />
          </Field>
          <Field label="Department">
            <Input value={form.department} onChange={(e) => set("department", e.target.value)} placeholder="e.g. Finance" className="bg-white border-slate-200 rounded-sm h-11" data-testid="ticket-department" />
          </Field>
          <Field label="Location">
            <Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="e.g. HQ Floor 3" className="bg-white border-slate-200 rounded-sm h-11" data-testid="ticket-location" />
          </Field>
          <Field label="Phone">
            <Input value={form.reporter_phone} onChange={(e) => set("reporter_phone", e.target.value)} placeholder="+254…" className="bg-white border-slate-200 rounded-sm h-11" data-testid="ticket-phone" />
          </Field>
          <Field label="Device / Asset tag">
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-2">
              {isStaff && assets.length > 0 && (
                <Select
                  value={form.asset_id}
                  onValueChange={(v) => {
                    const picked = assets.find((a) => a.asset_id === v);
                    setForm((prev) => ({ ...prev, asset_id: v, asset_tag: picked ? picked.asset_tag : prev.asset_tag }));
                  }}
                >
                  <SelectTrigger className="bg-white border-slate-200 rounded-sm h-11" data-testid="ticket-asset-select">
                    <SelectValue placeholder="Pick from KINOS asset registry…" />
                  </SelectTrigger>
                  <SelectContent>
                    {assets.map((a) => (
                      <SelectItem key={a.asset_id} value={a.asset_id} data-testid={`asset-option-${a.asset_tag}`}>
                        <span className="font-mono text-red-700">{a.asset_tag}</span> · {a.name} <span className="text-slate-500 text-[10px] ml-1">({a.asset_type})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Input value={form.asset_tag} onChange={(e) => set("asset_tag", e.target.value)} placeholder="…or type a tag freely" className="bg-white border-slate-200 rounded-sm h-11" data-testid="ticket-asset" />
            </div>
          </Field>
        </div>

        <div className="flex items-center justify-between border border-red-500/30 bg-red-500/5 p-4 rounded-sm">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 bg-red-500/20 flex items-center justify-center rounded-sm">
              <Sparkles className="h-4 w-4 text-red-600" strokeWidth={1.5} />
            </div>
            <div>
              <div className="text-sm font-medium">AI Triage</div>
              <div className="text-xs text-slate-600">Auto-fill category & priority when left blank.</div>
            </div>
          </div>
          <label className="flex items-center cursor-pointer">
            <input data-testid="ai-toggle" type="checkbox" checked={useAI} onChange={(e) => setUseAI(e.target.checked)} className="sr-only peer" />
            <div className="w-10 h-6 bg-slate-200 border border-slate-200 rounded-full peer peer-checked:bg-cyan-500 transition-colors relative after:content-[''] after:absolute after:top-[3px] after:left-[3px] after:bg-white after:h-4 after:w-4 after:rounded-full after:transition-all peer-checked:after:translate-x-4"></div>
          </label>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => nav(-1)} data-testid="cancel-btn" className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="submit-ticket-btn" className="bg-red-600 hover:bg-red-500 rounded-sm h-11 px-6">
            {submitting ? "Creating..." : "Create Ticket"}
          </Button>
        </div>
      </div>

      <InlineCustomerDialog open={custOpen} onOpenChange={setCustOpen} form={custForm} setCust={setCust} save={saveCustomer} saving={custSaving} />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">{label}</div>
      {children}
    </div>
  );
}

function InlineCustomerDialog({ open, onOpenChange, form, setCust, save, saving }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-slate-200 rounded-sm max-w-lg" data-testid="inline-customer-dialog">
        <DialogHeader><DialogTitle className="font-display">Add a customer profile</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-600 -mt-2 mb-2">Save this customer once — they&apos;ll be reusable across every future ticket.</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Full name *</div>
            <Input value={form.name} onChange={(e) => setCust("name", e.target.value)} placeholder="e.g. Ana Ximenes" className="bg-white border-slate-200 rounded-sm h-10" data-testid="inline-cust-name" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Email *</div>
            <Input value={form.email} onChange={(e) => setCust("email", e.target.value)} type="email" placeholder="ana@company.tl" className="bg-white border-slate-200 rounded-sm h-10" data-testid="inline-cust-email" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Phone</div>
            <Input value={form.phone} onChange={(e) => setCust("phone", e.target.value)} placeholder="+670 …" className="bg-white border-slate-200 rounded-sm h-10" data-testid="inline-cust-phone" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Organization</div>
            <Input value={form.organization} onChange={(e) => setCust("organization", e.target.value)} placeholder="e.g. Ministry of Finance" className="bg-white border-slate-200 rounded-sm h-10" data-testid="inline-cust-org" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Department</div>
            <Input value={form.department} onChange={(e) => setCust("department", e.target.value)} placeholder="e.g. Payroll" className="bg-white border-slate-200 rounded-sm h-10" data-testid="inline-cust-dept" />
          </div>
          <div className="md:col-span-2">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Location</div>
            <Input value={form.location} onChange={(e) => setCust("location", e.target.value)} placeholder="e.g. Dili HQ · Floor 3" className="bg-white border-slate-200 rounded-sm h-10" data-testid="inline-cust-loc" />
          </div>
        </div>
        <div className="flex items-center justify-between pt-4 border-t border-slate-200 mt-4">
          <label className="flex items-center gap-2 text-xs text-amber-300 cursor-pointer" data-testid="inline-cust-vip">
            <input type="checkbox" checked={form.vip} onChange={(e) => setCust("vip", e.target.checked)} className="accent-amber-400" />
            <Crown className="h-3.5 w-3.5" strokeWidth={1.8} /> Mark as VIP
          </label>
          <Button onClick={save} disabled={saving} data-testid="inline-cust-save" className="bg-red-600 hover:bg-red-500 rounded-sm">
            {saving ? "Saving…" : "Save & select"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
